import React, { useEffect, useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    FormControl,
    MenuItem,
    Select,
    Skeleton,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { SectionNames, Category, Key, CommentKey, CommentSectionName } from "constant";
import { fetchTabPageData } from "redux/slices/tabPagesOptionsSlice";
import { updateSustainabilityData, setSustainabilityError } from "redux/slices/updateDataSlice";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";
import { FroalaRichText } from "components/util/FroalaRichtext";
import { DapComment, updateDapCommentData } from "redux/slices/updateDapCommentSlice";
import { saveDapCommentData } from "redux/slices/saveCommentSlice";

const SustainabilityForm = (props) => {
    const dispatch = useAppDispatch();
      const assetPlanId = useAppSelector(
        (state) => state.getAssetPlanId.assetPlanId
      );
    const sustainabilityData = useAppSelector((state) => state.sustainabilityData);
    const updatedSustainabilityData:any = useAppSelector((state) => state.updateData?.sustainabilityData)
    const sectionOptions = useAppSelector((state) =>
    (state.dropdownAllOptions.tabData?.find(
        (tab) => tab.tabPageName === SectionNames.SUSTAINABILITY
    ))
    ) as any;

      const comments = useAppSelector((state) => state.dapComments?.data).find(
        (x) => x.commentKey.toLowerCase() === CommentKey.SUSTAINABILITY.toLowerCase()
      );


    useEffect(() => {
        if (!sectionOptions)
            dispatch(fetchTabPageData(SectionNames.SUSTAINABILITY));
    }, [dispatch, SectionNames.SUSTAINABILITY]);

  const handleCommentsChange = (newValue: string) => {
    const updatedcomment: DapComment = {
      id: comments ? comments.id : 0,
      commentKey: comments ? comments.commentKey : CommentKey.SUSTAINABILITY,
      commentText: newValue || "",
      sectionName: CommentSectionName.SUSTAINABILITY,
      assetPlanId: assetPlanId,
    };
    dispatch(updateDapCommentData(updatedcomment));
    dispatch(saveDapCommentData(updatedcomment));

  };


    const complianceStatusOptions = useMemo(() => {
        if (!sectionOptions?.data?.options) return [];

        const options = sectionOptions.data.options.find(
            opt => opt.key === "Sustainability_Compliance_Status_Comments"
        )?.options || [];

        // Create a copy of the array before sorting
        return [...options].sort((a, b) => a.order - b.order);
    }, [sectionOptions]);

    const decarbonizationAssessmentCommentOptions = useMemo(() => {
        if (!sectionOptions?.data?.options) return [];

        const options = sectionOptions.data.options.find(
            opt => opt.key === "Sustainability_Deracb_audit"
        )?.options || [];

        // Create a copy of the array before sorting
        return [...options].sort((a, b) => a.order - b.order);
    }, [sectionOptions]);




    const handleTextChange = (propertyId: string, field: string, value: string) => {

        dispatch(updateSustainabilityData({
            ...updatedSustainabilityData,
            [propertyId]: {
                ...updatedSustainabilityData?.[propertyId],
                [field]: value
            }
        }));
    };

    const tableStyles = {
        container: {
            maxHeight: 'calc(100vh - 200px)', // Adjust based on your header/footer height
            overflow: 'auto',
        },
        table: {
            '& .MuiTableCell-root': {
                height: '32px',
                margin: "0px",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                fontSize: "0.875rem",
                color: "rgba(0, 0, 0, 0.6)",
                padding: '4px 14px',
            },
            // Sticky header styles
            '& thead tr th': {
                position: 'sticky',
                backgroundColor: '#fff',
                zIndex: 2,
            },
            '& thead tr:first-of-type th': {
                top: 0,
            },
            '& thead tr:nth-of-type(2) th': {
                top: '33px', // Adjust based on your first row height
            },
            // Sticky first column (Property ID)
            '& .sticky-column': {
                position: 'sticky',
                left: 0,
                backgroundColor: '#cccccc',
                zIndex: 1,
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                }
            },
            // When header and first column intersect
            '& thead .sticky-column': {
                zIndex: 4,
            }
        },
        inputField: {
            '& .MuiInputBase-input': {
                color: 'rgba(0, 0, 0, 0.6)',
                fontSize: "0.875rem",
            },
            '& .MuiOutlinedInput-root': {
                '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                }
            }
        }

    };


    return (
        <div>
            <div className="tab-heading text-center">
                <h3 className="heading">Sustainability</h3>
            </div>
            <TableContainer sx={tableStyles.container}>
                {sustainabilityData.loading ? (
                    <Skeleton
                        variant="rounded"
                        width="100%"
                        height={300}
                        animation="wave"
                    />
                ) : (
                    <Table stickyHeader sx={tableStyles.table}>
                        <TableHead>
                            {/* Main Headers */}
                            <TableRow>
                                <TableCell
                                    align="center"
                                    rowSpan={2}  // This makes it span both header rows
                                    className="sticky-column table-main-header first-group-border group-border-right"
                                >
                                    Property ID
                                </TableCell>
                                <TableCell
                                    align="center"
                                    colSpan={8}
                                    className="table-main-header first-group-border group-border-right"
                                >
                                    Physical Risk Consideration
                                </TableCell>
                                <TableCell
                                    align="center"
                                    colSpan={7}
                                    className="table-main-header group-border-right"
                                >
                                    Regulatory Risk Consideration
                                </TableCell>
                                <TableCell
                                    align="center"
                                    colSpan={3}
                                    className="table-main-header group-border-right"
                                >
                                    Decarbonization Assessment
                                </TableCell>
                                <TableCell
                                    align="center"
                                    colSpan={3}
                                    className="table-main-header last-group-border"
                                >
                                    SSBB Minimum Standards
                                </TableCell>
                            </TableRow>
                            {/* Sub Headers */}
                            <TableRow>
                                {/* Sub headers for Main Heading 1 (7 columns) */}
                                <TableCell className="table-header-cell first-group-border">Current Exposure to Flood Risk</TableCell>
                                <TableCell className="table-header-cell">Current Exposure to Wildfire Risk</TableCell>
                                <TableCell className="table-header-cell">Current Exposure to Heat Stress</TableCell>
                                <TableCell className="table-header-cell">Current Exposure to Exposure to Drought Stress </TableCell>
                                <TableCell className="table-header-cell">Tropical Cyclone / Wind </TableCell>
                                <TableCell className="table-header-cell">Mitigation measures reported in the SSBB </TableCell>
                                <TableCell className="table-header-cell">Describe mitigation plans included in this budget, if any</TableCell>
                                <TableCell className="table-header-cell group-border-right">Optional comments</TableCell>

                                {/* Sub headers for Main Heading 2 (7 columns) */}
                                <TableCell className="table-header-cell">Current Exposure to Building Performance Standard</TableCell>
                                <TableCell className="table-header-cell">Current Exposure to Additional Regulatory Requirement</TableCell>
                                <TableCell className="table-header-cell">Compliance Status According to SSBB Record</TableCell>
                                <TableCell className="table-header-cell">Compliance Status Comments or Corrections</TableCell>
                                <TableCell className="table-header-cell">Does this budget include cost to procure a vendor for compliance plan?</TableCell>
                                <TableCell className="table-header-cell">Does this budget include projects to come into compliance with the regulation?</TableCell>
                                <TableCell className="table-header-cell group-border-right">Optional comments</TableCell>

                                {/* Sub headers for Main Heading 3 (3 columns) */}
                                <TableCell className="table-header-cell">Decarbonization assessment status according to SSBB record</TableCell>
                                <TableCell className="table-header-cell">Does this budget include the cost to complete a net zero audit?</TableCell>
                                <TableCell className="table-header-cell group-border-right">Optional comments</TableCell>

                                {/* Sub headers for Main Heading 4 (3 columns) */}
                                <TableCell className="table-header-cell">
                                    Are any SSBB features included in this budget? <span style={{ color: 'red' }}>*</span>
                                </TableCell>
                                <TableCell className="table-header-cell">
                                    List the SSBB features included in this budget
                                    {updatedSustainabilityData && Object.values(updatedSustainabilityData).some((data: any) => data?.ssbbStandards === 'yes') &&
                                        <span style={{ color: 'red' }}> *</span>
                                    }
                                </TableCell>
                                <TableCell className="table-header-cell last-group-border">Optional comments</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(sustainabilityData?.data?.sustainabilityData || {}).map(([propertyId, property]) => {
                                const propertyData = property as any;
                                return (
                                    <TableRow key={propertyId}>
                                        {/* Property ID Column */}
                                        <TableCell className="sticky-column table-cell first-group-border">
                                            {propertyId}
                                        </TableCell>
                                        {/* Physical Risk Assessment */}
                                        <TableCell className="table-cell first-group-border">
                                            <b>River (Fluvial) Flooding :</b>  {propertyData.riverFloodDefendedRisk}
                                            <br />
                                            <b>Sea Level Rise :</b> {propertyData.seaLevelRisk}

                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {propertyData.wildfireRisk}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {propertyData.heatStress}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {propertyData.droughtStress}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {propertyData.tropicalCyclone}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {propertyData.mitigationMeasures?.split(',').map((measure, index) => (
                                                <div key={index}>{measure.trim()}</div>
                                            ))}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <TextField
                                                sx={tableStyles.inputField}
                                                fullWidth
                                                variant="outlined"
                                                disabled={props.mode === "view"}
                                                size="small"
                                                multiline
                                                value={updatedSustainabilityData?.[propertyId]?.mitigationPlan || ''}
                                                onChange={(e) => handleTextChange(propertyId, 'mitigationPlan', e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell className="table-cell group-border-right">
                                            <TextField
                                                sx={tableStyles.inputField}
                                                fullWidth
                                                variant="outlined"
                                                disabled={props.mode === "view"}
                                                size="small"
                                                multiline
                                                value={updatedSustainabilityData?.[propertyId]?.physicalRiskComment || ''}
                                                onChange={(e) => handleTextChange(propertyId, 'physicalRiskComment', e.target.value)}
                                            />
                                        </TableCell>

                                        {/* Regulatory Risk Assessment */}
                                        <TableCell className="table-cell">
                                            {propertyData.buildingPerformanceStandard}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {propertyData.additionalRegulatoryReq}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {propertyData.complianceStatusAccToSsbb}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={updatedSustainabilityData?.[propertyId]?.complianceStatusComment === null ? "" : (updatedSustainabilityData?.[propertyId]?.complianceStatusComment || propertyData['complianceStatusComment'] || '')}
                                                    onChange={(e) => handleTextChange(propertyId, 'complianceStatusComment', e.target.value)}
                                                    variant="outlined"
                                                    disabled={props.mode === "view"}
                                                    displayEmpty
                                                    sx={tableStyles.inputField}
                                                >
                                                    <MenuItem disabled value="">
                                                        <em>Select</em>
                                                    </MenuItem>
                                                    {complianceStatusOptions.map((option) => (
                                                        <MenuItem
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.text}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={updatedSustainabilityData?.[propertyId]?.includeCostToProcedure === null ? "" : (updatedSustainabilityData?.[propertyId]?.includeCostToProcedure || propertyData['includeCostToProcedure'] || '')}
                                                    onChange={(e) => handleTextChange(propertyId, 'includeCostToProcedure', e.target.value)}
                                                    variant="outlined"
                                                    sx={tableStyles.inputField}
                                                    disabled={props.mode === "view"}
                                                >
                                                    <MenuItem disabled value="">
                                                        <em>Select</em>
                                                    </MenuItem>
                                                    <MenuItem value="yes">Yes</MenuItem>
                                                    <MenuItem value="no">No</MenuItem>
                                                    <MenuItem value="n/a">N/A</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={updatedSustainabilityData?.[propertyId]?.includeProjectsComesIntoCompliance === null ? "" : (updatedSustainabilityData?.[propertyId]?.includeProjectsComesIntoCompliance || propertyData['includeProjectsComesIntoCompliance'] || '')}
                                                    onChange={(e) => handleTextChange(propertyId, 'includeProjectsComesIntoCompliance', e.target.value)}
                                                    variant="outlined"
                                                    sx={tableStyles.inputField}
                                                    disabled={props.mode === "view"}
                                                >
                                                    <MenuItem disabled value="">
                                                        <em>Select</em>
                                                    </MenuItem>
                                                    <MenuItem value="yes">Yes</MenuItem>
                                                    <MenuItem value="no">No</MenuItem>
                                                    <MenuItem value="n/a">N/A</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell className="table-cell group-border-right">
                                            <TextField
                                                sx={tableStyles.inputField}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                disabled={props.mode === "view"}
                                                multiline
                                                value={updatedSustainabilityData?.[propertyId]?.regulatoryRiskComment || ''}
                                                onChange={(e) => handleTextChange(propertyId, 'regulatoryRiskComment', e.target.value)}
                                            />
                                        </TableCell>

                                        {/* Decarbonization Assessment */}
                                        <TableCell className="table-cell">
                                            {propertyData.decarbonizationAssessmentStatus}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={updatedSustainabilityData?.[propertyId]?.decarbonizationAssessmentComment === null ? "" : (updatedSustainabilityData?.[propertyId]?.decarbonizationAssessmentComment || '')}
                                                    onChange={(e) => handleTextChange(propertyId, 'decarbonizationAssessmentComment', e.target.value)}
                                                    variant="outlined"
                                                    disabled={props.mode === "view"}
                                                    displayEmpty
                                                    sx={tableStyles.inputField}
                                                >
                                                    <MenuItem disabled value="">
                                                        <em>Select</em>
                                                    </MenuItem>
                                                    {decarbonizationAssessmentCommentOptions.map((option) => (
                                                        <MenuItem
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.text}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell className="table-cell group-border-right">
                                            <TextField
                                                sx={tableStyles.inputField}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                disabled={props.mode === "view"}
                                                multiline
                                                value={updatedSustainabilityData?.[propertyId]?.decarbonizationAssessmentStatusComment || ''}
                                                onChange={(e) => handleTextChange(propertyId, 'decarbonizationAssessmentStatusComment', e.target.value)}
                                            />
                                        </TableCell>

                                        {/* SSBB Minimum Standards */}
                                        <TableCell className="table-cell">
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={updatedSustainabilityData?.[propertyId]?.ssbbStandards === null ? "" : (updatedSustainabilityData?.[propertyId]?.ssbbStandards || propertyData['ssbbStandards'] || '')}
                                                    onChange={(e) => handleTextChange(propertyId, 'ssbbStandards', e.target.value)}
                                                    variant="outlined"
                                                    disabled={props.mode === "view"}
                                                    sx={tableStyles.inputField}
                                                >
                                                    <MenuItem disabled value="">
                                                        <em>Select</em>
                                                    </MenuItem>
                                                    <MenuItem value="yes">Yes</MenuItem>
                                                    <MenuItem value="no">No</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <TextField
                                                sx={tableStyles.inputField}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                disabled={props.mode === "view"}
                                                multiline
                                                value={updatedSustainabilityData?.[propertyId]?.ssbbFeatures || ''}
                                                onChange={(e) => handleTextChange(propertyId, 'ssbbFeatures', e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell className="table-cell last-group-border">
                                            <TextField
                                                sx={tableStyles.inputField}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                disabled={props.mode === "view"}
                                                multiline
                                                value={updatedSustainabilityData?.[propertyId]?.ssbbStandardsComment || ''}
                                                onChange={(e) => handleTextChange(propertyId, 'ssbbStandardsComment', e.target.value)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            
                     <form className="m-20 lease-form">
                       <fieldset>
                         <legend className="lease-title">Comments</legend>
                         <>
                           {props?.mode === "edit" && (
                             <FroalaRichText
                               model={comments?.commentText || ""}
                               handlesModelChange={handleCommentsChange}
                             />
                           )}
               
                           {(props?.mode === undefined || props?.mode === "view") && (
                             <FroalaEditorView model={comments?.commentText || ""} />
                           )}
                         </>
                       </fieldset>
                     </form>
        </div>
    );
};

// Export the validation function so it can be used by the parent component
export { SustainabilityForm as default };
export const validateSustainabilityData = (sustainabilityData, editedData) => {
    const errors: string[] = [];
    Object.entries(sustainabilityData?.data?.sustainabilityData || {}).forEach(([propertyId, property]) => {
        const propertyData = property as any;
        const currentData = editedData[propertyId] || {};
        const ssbbStandards = currentData.ssbbStandards || propertyData['ssbbStandards'];
        const ssbbFeatures = currentData.ssbbFeatures || propertyData['ssbbFeatures'];

        if (!ssbbStandards) {
            errors.push(`Property ${propertyId}: SSBB features selection is required`);
        } else if (ssbbStandards === 'yes' && (!ssbbFeatures || !ssbbFeatures.trim())) {
            errors.push(`Property ${propertyId}: List of SSBB features is required when Yes is selected`);
        }
    });
    return errors;
};
